import { getDataSource } from '@/lib/data-source';
import { ApiError } from '@/lib/data-source/types';
import { NodeType } from '@/types/automation';

export interface AutomationNode {
  id: string;
  product_id: string;
  node_id: string;
  name: string;
  type: NodeType;
  configuration?: Record<string, unknown>;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAutomationNodeData {
  name: string;
  type: NodeType;
  configuration?: Record<string, unknown>;
  is_active?: boolean;
}

export interface UpdateAutomationNodeData extends Partial<CreateAutomationNodeData> {}

function isNotFoundError(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'status' in error &&
    (error as ApiError).status === 404
  );
}

function isNetworkError(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'status' in error &&
    (error as ApiError).status === 0
  );
}

function normalizeAutomationNode(rawNode: unknown): AutomationNode {
  if (!rawNode || typeof rawNode !== 'object') {
    throw new Error('Invalid automation node payload');
  }

  const node = rawNode as Record<string, unknown>;
  const id = node.id ?? node.node_id ?? node.nodeId;
  if (!id || typeof id !== 'string') {
    throw new Error('Automation node payload missing id');
  }

  const productId = node.product_id ?? node.productId ?? '';
  const nodeId = node.node_id ?? node.nodeId ?? '';
  const name = node.name ?? '';
  const type = (node.type as NodeType) ?? 'billing';
  const createdAt = node.created_at ?? node.createdAt ?? new Date().toISOString();
  const updatedAt = node.updated_at ?? node.updatedAt ?? createdAt;

  return {
    id,
    product_id: typeof productId === 'string' ? productId : '',
    node_id: typeof nodeId === 'string' ? nodeId : String(nodeId),
    name: typeof name === 'string' ? name : String(name),
    type: type === 'report' ? 'report' : 'billing',
    configuration: (node.configuration as Record<string, unknown>) ?? undefined,
    is_active: typeof node.is_active === 'boolean'
      ? node.is_active
      : typeof node.isActive === 'boolean'
        ? node.isActive
        : true,
    created_at: typeof createdAt === 'string' ? createdAt : String(createdAt),
    updated_at: typeof updatedAt === 'string' ? updatedAt : String(updatedAt),
  };
}

function extractNodes(payload: unknown): AutomationNode[] {
  if (!payload) {
    return [];
  }

  const maybeArray = Array.isArray(payload) ? payload :
    Array.isArray((payload as { data?: unknown }).data) ? (payload as { data: unknown[] }).data :
    [];

  return maybeArray.reduce<AutomationNode[]>((acc, item) => {
    try {
      acc.push(normalizeAutomationNode(item));
    } catch (error) {
      console.warn('Skipping automation node due to invalid payload', error);
    }
    return acc;
  }, []);
}

function extractNode(payload: unknown): AutomationNode {
  const source = (payload as { data?: unknown }).data ?? payload;
  return normalizeAutomationNode(source);
}

function extractMessage(payload: unknown): { message: string } {
  if (payload && typeof payload === 'object' && 'message' in payload && typeof (payload as { message: unknown }).message === 'string') {
    return payload as { message: string };
  }
  return { message: 'Automation node deleted successfully' };
}

class AutomationNodesService {
  private dataSource = getDataSource();

  async getProductNodes(productId: string): Promise<AutomationNode[]> {
    try {
      const response = await this.dataSource.get<unknown>(`/provider/products/${productId}/automation-nodes`);
      return extractNodes(response.data);
    } catch (error) {
      if (isNotFoundError(error) || isNetworkError(error)) {
        console.warn(`No automation nodes available for product ${productId}.`, error);
        return [];
      }
      console.error(`Error fetching automation nodes for product ${productId}:`, error);
      throw error;
    }
  }

  async createNode(productId: string, data: CreateAutomationNodeData): Promise<AutomationNode> {
    try {
      const response = await this.dataSource.post<unknown>(`/provider/products/${productId}/automation-nodes`, data);
      return extractNode(response.data);
    } catch (error) {
      console.error(`Error creating automation node for product ${productId}:`, error);
      throw error;
    }
  }

  async updateNode(productId: string, nodeId: string, data: UpdateAutomationNodeData): Promise<AutomationNode> {
    try {
      const response = await this.dataSource.put<unknown>(`/provider/products/${productId}/automation-nodes/${nodeId}`, data);
      return extractNode(response.data);
    } catch (error) {
      console.error(`Error updating automation node ${nodeId}:`, error);
      throw error;
    }
  }

  async deleteNode(productId: string, nodeId: string): Promise<{ message: string }> {
    try {
      const response = await this.dataSource.delete<unknown>(`/provider/products/${productId}/automation-nodes/${nodeId}`);
      return extractMessage(response.data);
    } catch (error) {
      console.error(`Error deleting automation node ${nodeId}:`, error);
      throw error;
    }
  }

  async toggleNodeStatus(productId: string, nodeId: string): Promise<AutomationNode> {
    try {
      const response = await this.dataSource.post<unknown>(`/provider/products/${productId}/automation-nodes/${nodeId}/toggle`);
      return extractNode(response.data);
    } catch (error) {
      console.error(`Error toggling automation node ${nodeId}:`, error);
      throw error;
    }
  }
}

export const automationNodesService = new AutomationNodesService();